// Quick entry form for bathroom events (BM)
import React, { useState } from 'react';
import { healthAPI } from '../api';
import { BathroomMetadata, LocationData } from '../types';
import { LocationInput } from './LocationInput';
import './QuickEntry.css';

interface BathroomFormProps {
  onSuccess?: (traceId: string) => void;
  onError?: (error: string) => void;
}

// Bristol Scale detailed descriptions
const BRISTOL_SCALE_INFO = [
  { scale: 1, emoji: '🔴', name: 'Separate hard lumps', description: 'Like nuts (severe constipation)', color: '#8B4513' },
  { scale: 2, emoji: '🟤', name: 'Lumpy & sausage-like', description: 'Hard and difficult to pass', color: '#A0522D' },
  { scale: 3, emoji: '🟫', name: 'Sausage with cracks', description: 'Normal but slightly hard', color: '#D2691E' },
  { scale: 4, emoji: '🟤', name: 'Smooth & soft sausage', description: 'Ideal consistency', color: '#CD853F' },
  { scale: 5, emoji: '🟡', name: 'Soft blobs', description: 'Soft with clear edges', color: '#DAA520' },
  { scale: 6, emoji: '🟠', name: 'Mushy pieces', description: 'Fluffy with ragged edges', color: '#FF8C00' },
  { scale: 7, emoji: '💧', name: 'Liquid', description: 'Watery, no solids (diarrhea)', color: '#FFA500' },
];

export const BathroomForm: React.FC<BathroomFormProps> = ({ onSuccess, onError }) => {
  const [bristolScale, setBristolScale] = useState<number>(4);
  const [pain, setPain] = useState<number>(0);
  const [blood, setBlood] = useState<boolean>(false);
  const [urgency, setUrgency] = useState<'normal' | 'moderate' | 'high'>('normal');
  const [notes, setNotes] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [location, setLocation] = useState<LocationData | undefined>(undefined);

  const getCurrentScaleInfo = () => BRISTOL_SCALE_INFO[bristolScale - 1];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const data: BathroomMetadata = {
        bristol_scale: bristolScale,
        pain: pain > 0 ? pain : undefined,
        blood,
        urgency,
        notes: notes.trim() || undefined,
        location,
      };

      const requestBody: any = {
        type: 'bathroom',
        data,
      };

      const response = await healthAPI.createEvent(requestBody);

      // Reset form
      setBristolScale(4);
      setPain(0);
      setBlood(false);
      setUrgency('normal');
      setNotes('');
      setLocation(undefined);

      if (onSuccess) {
        onSuccess(response.trace_id);
      }
    } catch (error) {
      console.error('Error logging bathroom event:', error);
      if (onError) {
        onError(error instanceof Error ? error.message : 'Unknown error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="quick-entry-form bathroom-form">
      <h3>🚽 Bathroom Event</h3>

      <div className="form-group">
        <label>Bristol Stool Scale (1-7)</label>
        <div className="bristol-scale-buttons">
          {BRISTOL_SCALE_INFO.map((info) => (
            <button
              key={info.scale}
              type="button"
              className={`bristol-button ${bristolScale === info.scale ? 'active' : ''}`}
              onClick={() => setBristolScale(info.scale)}
              style={{
                borderColor: bristolScale === info.scale ? info.color : '#ddd',
                backgroundColor: bristolScale === info.scale ? info.color : '#f8f8f8',
                color: bristolScale === info.scale ? '#fff' : '#333',
              }}
              title={`${info.name}: ${info.description}`}
            >
              <div className="bristol-emoji">{info.emoji}</div>
              <div className="bristol-number">{info.scale}</div>
            </button>
          ))}
        </div>
        <div className="bristol-detail">
          <div className="bristol-detail-name">
            <strong>Type {getCurrentScaleInfo().scale}:</strong> {getCurrentScaleInfo().name}
          </div>
          <div className="bristol-detail-description">
            {getCurrentScaleInfo().description}
          </div>
          <div className="bristol-health-note">
            {bristolScale <= 2 && '⚠️ Constipation - Consider increasing fiber and water'}
            {bristolScale >= 3 && bristolScale <= 5 && '✅ Normal, healthy stool'}
            {bristolScale === 6 && '⚠️ Mild diarrhea - Monitor hydration'}
            {bristolScale === 7 && '🚨 Severe diarrhea - Stay hydrated, contact doctor if persistent'}
          </div>
        </div>
      </div>

      <div className="form-group">
        <label>Pain Level (0-10)</label>
        <input
          type="range"
          min="0"
          max="10"
          value={pain}
          onChange={(e) => setPain(parseInt(e.target.value))}
          className="pain-slider"
        />
        <span className="pain-value">{pain}</span>
      </div>

      <div className="form-group checkbox-group">
        <label>
          <input
            type="checkbox"
            checked={urgency === 'high'}
            onChange={(e) => setUrgency(e.target.checked ? 'high' : 'normal')}
          />
          Urgent
        </label>
        <label>
          <input
            type="checkbox"
            checked={blood}
            onChange={(e) => setBlood(e.target.checked)}
          />
          Blood present
        </label>
      </div>

      <div className="form-group">
        <label>Notes / Context</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="E.g. 'Stomach ache after lunch', 'Yesterday morning', 'At the office'"
          rows={2}
        />
        <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>
          Mention time (e.g. "8am") or location if you're elsewhere.
        </div>
      </div>

      <LocationInput onLocationChange={setLocation} initialLocation={location} />

      <button type="submit" className="submit-button" disabled={isSubmitting}>
        {isSubmitting ? 'Logging...' : 'Log Event'}
      </button>
    </form>
  );
};
